// client/src/config/pagePermissions.ts
//
// SINGLE SOURCE OF TRUTH for which permission key a page/route requires.
//
// Used by:
//   - ProtectedRoute  -> blocks direct navigation to a page the user can't access
//   - EnhancedSidebar -> hides nav entries the user can't access
//   - SettingsHome    -> hides settings cards the user can't access
//
// The keys here MUST match `permission_key` values in the `permissions` DB table
// (see database/migrations/011_phase3_permissions.sql and 030_page_permissions.sql).
// Admin-tier roles resolve to ['*'] server-side, so they always pass.
//
// Matching is longest-prefix: a request for `/order-management/orders/create`
// resolves to the `/order-management` entry, while the more specific
// `/settings/organization/users/roles` wins over the generic `/settings` entry.
//
// A value is one permission key, or an array of keys meaning ANY of them opens
// the page. Check it with `permits(required, hasPermission)`, never by calling
// hasPermission() on the value directly.

/** One permission key, or alternatives any one of which opens the page. */
export type RequiredPermission = string | readonly string[];

/** `hasPermission(key)` from AuthContext (admin tiers hold '*' and pass every key). */
export type PermissionCheck = (key: string) => boolean;

// path -> required permission key
const ROUTE_PERMISSION_MAP: Readonly<Record<string, RequiredPermission>> = {
  // Operations
  '/route-summary': 'routes.view',
  '/product-movement': 'product_movement.view',
  '/period-summary': 'period_summary.view',
  '/location-analytics': 'analytics.view',
  '/transaction-search': 'transaction_search.view',
  '/vehicle-tracker': 'vehicle_tracker.view',
  '/geofencing': 'geofencing.view',
  // Live View (ST-00210): the live map, once the Geofences page's first tab — same key.
  '/lexistar/live': 'geofencing.view',
  // LexiStar control tower (ST-00188 M2-U). Deciding on deviations additionally
  // needs lexistar.deviations.approve, overriding lexistar.trips.manage (in-page).
  '/lexistar/status-board': 'lexistar.trips.view',
  // ST-00249: maintenance bookings, breakdowns and delays, and their codes in Settings → LexiStar.
  // Booking and changing need lexistar.maintenance.manage (in-page).
  '/lexistar/maintenance': ['lexistar.maintenance.view', 'lexistar.maintenance.manage'],
  '/lexistar/approvals': 'lexistar.trips.view',
  '/lexistar/trips': 'lexistar.trips.view',
  '/lexistar/plan': 'lexistar.trips.manage',
  // ST-00250: the dispatch planning board (order bubbles, trucks dragged in and out). Reading is
  // trips.view; adding orders, allocating and releasing need lexistar.trips.manage (in-page).
  '/lexistar/dispatch-board': 'lexistar.trips.view',
  // LexiStar assurance (ST-00188 M3-U1). Recompute jobs open with either key:
  // .run starts jobs, .signoff publishes them (the second user must be able to
  // open a job it did not start); trip sign-off and amendments are in-page on
  // the trip (lexistar.trips.signoff / .amend).
  '/lexistar/review': 'lexistar.review.triage',
  '/lexistar/recompute-jobs': ['lexistar.recompute.run', 'lexistar.recompute.signoff'],
  '/lexistar/commissioning': 'lexistar.commissioning.manage',
  '/lexistar/declarations': 'lexistar.declarations.manage',
  // LexiStar operations (ST-00188 M3-U2). Managing drivers / allocations needs
  // lexistar.drivers.manage and changing rules lexistar.notifications.manage (in-page).
  '/lexistar/plan-board': 'lexistar.trips.view',
  // ST-00318: Replay's tabs — the track replay and the idling heatmap need lexistar.replay.view, the
  // Trips tab (every vehicle's GPS trips, once the Geofences page's) geofencing.view, as its API
  // does. Either opens the page; it shows the tabs the viewer may use (Replay.tsx).
  '/lexistar/replay': ['lexistar.replay.view', 'geofencing.view'],
  '/lexistar/drivers': 'lexistar.drivers.view',
  '/lexistar/messages': 'lexistar.messages.view',
  // ST-00251: the notifications inbox — the bell's keys (any LexiStar user who gets alerts).
  '/lexistar/notifications': ['lexistar.trips.view', 'lexistar.notifications.view'],
  // LexiStar reports (ST-00188 M4-R2): trip report, run / daily summary, standard
  // reports, Reports centre. Schedules additionally need lexistar.reports.manage (in-page).
  '/lexistar/reports': 'lexistar.reports.view', // M4-R2
  // LexiStar numbers (ST-00188 M4-D). The idling heatmap is Replay's second tab
  // (lexistar.replay.view); /lexistar/trips (the drill-through list) is above.
  '/lexistar/dashboard': 'lexistar.dashboard.view', // M4-D
  '/lexistar/kpis': 'lexistar.kpis.view', // M4-D
  // ST-00254/255/256: fuel vs allowance, tonnes, speeding by route / truck / driver. The driver
  // dimension additionally needs lexistar.drivers.view (in-page).
  '/lexistar/routes-trucks-drivers': 'lexistar.kpis.view',
  '/lexistar/dwell': 'lexistar.kpis.view', // M4-D
  // Fleet & targets (ST-00247 / ST-00248): reading is the KPIs key; setting targets needs
  // lexistar.reports.manage (in-page).
  '/lexistar/fleet-performance': 'lexistar.kpis.view',
  // Usage & billing (ST-00207 M4b-A): statements + the printable statement page. Plans,
  // finalise and rebuild additionally need lexistar.billing.manage and the global-admin role (in-page).
  // Sidebar: LexiStar · Assure & configure since ST-00240 (was Analyse); the URL is unchanged.
  '/lexistar/billing': 'lexistar.billing.view', // M4b-A
  '/custom-summaries': 'summaries.view',
  '/order-management': 'orders.view',

  // Amendments / transactions
  '/amendments-allocate-transactions': 'amendments.view',
  '/amendment-approvals': 'amendment_approvals.view',
  '/amendment-analytics': 'amendment_analytics.view',
  '/transaction-allocation': 'transactions.view',

  // Reporting / docs
  '/pdf-generator-v2': 'pdf.generate',
  '/signatures': 'signatures.view',
  '/email-reports': 'email.view',

  // Monitoring / intelligence
  '/error-detection': 'errors.view',
  // The monitors need route_intelligence.view; the Load matching tab (ST-00218) opens the page
  // for customer reviewers with load_matching.view only (each tab gates itself, in-page).
  '/route-intelligence': ['route_intelligence.view', 'load_matching.view'],
  '/telegram-bi': 'telegram.view',
  '/edge-sync': 'edge_sync.view',
  '/weighbridge-compliance': 'weighbridge_compliance.view',
  // Sidebar link into the embedded lexadapt-support platform (/support/, served
  // by nginx — not a React route). Ticketing itself lives on the platform.
  // ST-00283: every signed-in user (was support.view). The floating "Log a support
  // ticket" button is gone and this menu entry ("+ New Ticket" on the platform) is the
  // only way to log a ticket, so nobody may lose it; the token exchange behind it
  // (POST /api/auth/support-token) never needed more than a session.
  '/support': [],
  // Help & training (ST-00200): every signed-in user. An empty list is "no key needed";
  // guides for pages a user cannot open stay readable, their buttons greyed out (in-page).
  '/help': [],

  // Settings — specific admin sub-pages (more specific than the generic /settings)
  // ST-00224: the user and role pages also open for a holder of one of their action
  // keys (the server lets those holders read the lists); each action gates itself in-page.
  '/settings/organization/users/manage': [
    'users.view',
    'users.invite',
    'users.assign_roles',
    'users.manage',
    'users.remove',
  ],
  '/settings/organization/users/roles': [
    'roles.view',
    'roles.create',
    'roles.edit',
    'roles.delete',
    'roles.assign_permissions',
    'roles.manage',
  ],
  // Organization Details: editing needs tenant.edit or settings.edit (in-page).
  '/settings/organization/account-setup/tenant': ['tenant.view', 'tenant.edit', 'settings.edit'],
  // Branding: the brand logo needs settings.edit (in-page); the library is open to the page.
  '/settings/organization/branding/logo': ['settings.view', 'settings.edit'],
  '/settings/organization/account-setup/tenant-management': 'tenant.view',
  '/settings/organization/account-setup/subscription': 'subscription.view',
  // The Settings hub's LexiStar card (ST-00240/ST-00252). Its pages keep their
  // /settings/modules/monitoring/... addresses (bookmarks, review-queue fix links); the two
  // links out of Settings carry the permission of the page they open, so the card is shown,
  // and the redirect followed, only by people who can open that page.
  '/settings/modules/monitoring/geofencing': 'geofencing.view',
  '/settings/modules/lexistar/usage-billing': 'lexistar.billing.view',
  '/settings/modules/lexistar/maintenance-codes': ['lexistar.maintenance.view', 'lexistar.maintenance.manage'], // ST-00249
  '/settings/modules/lexistar/targets': 'lexistar.kpis.view', // ST-00248: opens Fleet & targets → Targets
  // ST-00276: who sees the fleet reads the map colours; changing them needs .notifications.manage (in-page).
  '/settings/modules/lexistar/map-colours': ['lexistar.trips.view', 'lexistar.notifications.manage'],
  '/settings/modules/monitoring/telemetry-devices': 'telemetry.view',
  '/settings/modules/monitoring/lexistar-assets': 'lexistar.assets.view',
  // LexiStar setup (M2-A1). Fuel: there is no lexistar.fuel.view key, and trip
  // planners read the norms; changing them needs lexistar.fuel.manage (in-page).
  '/settings/modules/monitoring/lexistar-routes': 'lexistar.routes.view',
  '/settings/modules/monitoring/lexistar-fuel': 'lexistar.trips.view',
  // LexiStar notification setup (M3-U2); reached from Settings → LexiStar (its sidebar entry,
  // added in ST-00252, was taken out again on 2026-10-29 in the same ticket).
  '/settings/modules/monitoring/lexistar-notifications': 'lexistar.notifications.view', // M3-U2
  '/settings/modules/monitoring/lexistar-webhooks': 'lexistar.integrations.view', // ST-00207 M4b-B
  '/settings/modules/monitoring/lexistar-export': 'lexistar.export.manage', // ST-00207 M4b-C
  // Generic settings catch-all (lowest precedence by virtue of being shortest)
  '/settings': 'settings.view',
};

// Pre-sort the paths longest-first so the first prefix hit is the most specific.
const SORTED_PATHS = Object.keys(ROUTE_PERMISSION_MAP).sort((a, b) => b.length - a.length);

/**
 * Resolve the permission required to view `pathname` — one key, or an array of
 * keys any of which is enough — or null if the page is open to any
 * authenticated user (e.g. the `/` home / operations overview).
 */
export function getRequiredPermission(
  pathname: string | null | undefined
): RequiredPermission | null {
  if (!pathname) return null;
  for (const path of SORTED_PATHS) {
    if (pathname === path || pathname.startsWith(path + '/')) {
      return ROUTE_PERMISSION_MAP[path] ?? null;
    }
  }
  return null;
}

// Array.isArray does not narrow a readonly array out of a union (TS #17002).
const isKeyList = (v: RequiredPermission): v is readonly string[] => Array.isArray(v);

/**
 * Whether `required` (a key, an array of alternative keys, or null) is met by a
 * `hasPermission(key)` check. Null / empty means open to any authenticated user.
 */
export function permits(
  required: RequiredPermission | null | undefined,
  hasPermission: PermissionCheck
): boolean {
  if (!required || (isKeyList(required) && required.length === 0)) return true;
  const keys: readonly string[] = isKeyList(required) ? required : [required];
  return keys.some((key) => hasPermission(key));
}

export { ROUTE_PERMISSION_MAP };
